import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  deleteEmployee,
  fetchEmployeeOnboardingStatus,
  putEmployeeOnboardingStatus,
  fetchContractorOnboardingStatus,
  putContractorOnboardingStatus,
  deleteContractor,
} from '../payrollActions'
import { Button, Dialog, Text } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { selectTeamMemberByUuid } from '../payroll.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  Gusto_EmployeeContractorOnboarding,
  Gusto_EmployeeContractorOnboardingStatus,
} from '../generated_gusto_types'
import { OnboardEmployeeModal } from './OnboardEmployeeModal'

// This modal is for employees only since it's a more complex flow

export const OnboardingCTA = ({
  teamMemberUuid,
  isEmployee,
}: {
  teamMemberUuid: string
  isEmployee: boolean
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [onboardingState, setOnboardingState] =
    useState<Gusto_EmployeeContractorOnboarding>()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false)

  const teamMember = useReselector(
    selectTeamMemberByUuid,
    teamMemberUuid,
    isEmployee
  )

  // Lists the missing required steps for onboarding an employee
  const requiredIncompleteSteps = useMemo(
    () =>
      (onboardingState?.onboarding_steps || []).filter(
        (step) => !step.completed && step.required
      ),
    [onboardingState?.onboarding_steps]
  )

  // Contractors will automatically go into onboarded state once flow is complete.  Employees require another few steps
  const showCompleteOnboarding =
    isEmployee &&
    onboardingState?.onboarding_status ===
      'self_onboarding_awaiting_admin_review'

  const deleteOnboardingTeamMember = useCallback(async () => {
    if (isEmployee) {
      await deleteEmployee(teamMemberUuid)(dispatch)
    } else {
      await deleteContractor(teamMemberUuid)(dispatch)
    }
    navigate('/payroll/employees')
  }, [teamMemberUuid, isEmployee, dispatch, navigate])

  // Fetch team member onboarding status
  useEffect(() => {
    const fetch = async () => {
      const res = isEmployee
        ? await fetchEmployeeOnboardingStatus(teamMemberUuid)(dispatch)
        : await fetchContractorOnboardingStatus(teamMemberUuid)(dispatch)

      if (res) {
        setOnboardingState(res)
      }
    }
    if (!teamMember?.onboarded) {
      fetch()
    }
  }, [dispatch, isEmployee, teamMember?.onboarded, teamMemberUuid])

  useEffect(() => {
    const updateTeamMemberStatus = async (
      newStatus: Gusto_EmployeeContractorOnboardingStatus
    ) => {
      const res = isEmployee
        ? await putEmployeeOnboardingStatus(teamMemberUuid, newStatus)(dispatch)
        : await putContractorOnboardingStatus(
            teamMemberUuid,
            newStatus
          )(dispatch)

      if (res) {
        setOnboardingState(res)
      }
    }

    // https://docs.gusto.com/embedded-payroll/reference/put-v1-employees-employee_id-onboarding_status
    // https://docs.gusto.com/embedded-payroll/reference/put-v1-contractors-contractor_uuid-onboarding_status
    switch (onboardingState?.onboarding_status) {
      case 'admin_onboarding_incomplete':
      case 'self_onboarding_pending_invite':
      case 'self_onboarding_not_invited':
        // Update team member onboarding status to 'self_onboarding_invited' to allow them to onboard
        updateTeamMemberStatus('self_onboarding_invited')
        break
      case 'self_onboarding_completed_by_employee':
        updateTeamMemberStatus('self_onboarding_awaiting_admin_review')
        break
      default:
        break
    }
  }, [dispatch, isEmployee, onboardingState?.onboarding_status, teamMemberUuid])

  if (!teamMember || teamMember?.onboarded) {
    return null
  }

  const teamMemberText = isEmployee ? 'Employee' : 'Contractor'

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <div className="lg:w-1/2">
        {onboardingState &&
          onboardingState.onboarding_status !== 'onboarding_completed' && (
            <>
              <Text>
                <b>Missing steps:</b>
              </Text>
              {requiredIncompleteSteps.map((step) => (
                <Text key={step.id} className="mb-1">
                  &nbsp;&nbsp;&nbsp;&nbsp;&bull; {step.title}
                </Text>
              ))}
            </>
          )}
      </div>

      {showCompleteOnboarding && (
        <>
          <div className="lg:w-3/12">
            <Button onClick={() => setOnboardingModalOpen(true)} variant="link">
              Complete onboarding
            </Button>
          </div>
          <OnboardEmployeeModal
            open={onboardingModalOpen}
            onClose={() => setOnboardingModalOpen(false)}
            employeeUuid={teamMemberUuid}
          />
        </>
      )}

      <div className="lg:w-1/6">
        <Button variant="danger" onClick={() => setDeleteModalOpen(true)}>
          Delete {teamMemberText}
        </Button>
      </div>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <Dialog.Content className="max-w-sm">
          <Dialog.Title>Delete {teamMemberText}</Dialog.Title>
          <Dialog.Description>
            <Text>
              Please confirm that you would like to remove this {teamMemberText}
              . Deleting a team member is an action that cannot be reversed.
            </Text>
          </Dialog.Description>
          <Dialog.Footer>
            <Button
              variant="secondary"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={deleteOnboardingTeamMember} variant="danger">
              Delete {teamMemberText}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </div>
  )
}
